import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  ChoiceList,
  IndexFilters,
  IndexFiltersMode,
  IndexTable,
} from "@shopify/polaris";
import type { IndexFiltersProps, FilterInterface } from "@shopify/polaris";
import { shopsCollection } from "../../../firebase/db";
import { useAppDispatch } from "../../../redux/hooks";
import { setEmulating } from "../../../redux/slices/env";
import ShopsRow from "./ShopsRow";
import { SearchParamsAdmin } from "@/shared/refs/paths";
import { AppPlanNames } from "@/shared/typings/enums/app";
import type { DbShop } from "@/shared/typings/types/shop";
import { getDocs, limit, orderBy, query, startAt, where, QueryDocumentSnapshot, documentId } from "firebase/firestore";
import type { OrderByDirection } from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";

enum FilterKeys {
  appInstalled = "appInstalled",
  appPlanActive = "appPlanActive",
  appPlanName = "appPlanName",
}

type FilterValues = {
  [FilterKeys.appInstalled]: boolean | "";
  [FilterKeys.appPlanActive]: boolean | "";
  [FilterKeys.appPlanName]: string;
};

export default function Shops() {
  const [shops, shopsSet] = useState<DbShop[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState<{ [key: number]: QueryDocumentSnapshot<DbShop> }>({});
  const [sortBy, setSortBy] = useState(["appOpenedLast desc"]);
  const [mode, setMode] = useState(IndexFiltersMode.Filtering);
  const [queryValue, setQueryValue] = useState("");
  const [shopDomain, setShopDomain] = useState("");
  const [searchQueryTimeout, setSearchQueryTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [filterValues, setFilterValues] = useState<FilterValues>({
    appInstalled: "",
    appPlanActive: "",
    appPlanName: "",
  });
  const [search] = useSearchParams();
  const searchQuery = search.get(SearchParamsAdmin.QUERY);
  const emulateQuery = search.get(SearchParamsAdmin.EMULATE);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const pageSize = 50;
  const { appInstalled, appPlanActive, appPlanName } = filterValues;

  const sortOptions: IndexFiltersProps["sortOptions"] = [
    { label: "Last Seen", value: "appOpenedLast asc", directionLabel: "Least recent" },
    { label: "Last Seen", value: "appOpenedLast desc", directionLabel: "Most recent" },
    { label: "Plan name", value: "appPlanName asc", directionLabel: "Ascending" },
    { label: "Plan name", value: "appPlanName desc", directionLabel: "Descending" },
    { label: "Plan active", value: "appPlanActive asc", directionLabel: "Active" },
    { label: "Plan active", value: "appPlanActive desc", directionLabel: "Inactive" },
    { label: "Installed", value: "appInstalled asc", directionLabel: "Installed" },
    { label: "Installed", value: "appInstalled desc", directionLabel: "Installed" },
  ];

  const hasNextPage = useMemo(() => !!cursors[page + 1], [cursors, page]);

  const handleFilterSet = useCallback((key: string, value: string | boolean) => {
    setFilterValues(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleFilterRemove = useCallback((key: string) => {
    setFilterValues(prev => ({ ...prev, [key]: "" }));
  }, []);

  const handleFilterRemoveAll = useCallback(() => {
    setFilterValues({
      appInstalled: "",
      appPlanActive: "",
      appPlanName: "",
    });
  }, []);

  useEffect(() => {
    if (searchQuery) {
      setShopDomain(searchQuery);
      setQueryValue(searchQuery);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (emulateQuery) {
      dispatch(setEmulating(emulateQuery));
      navigate('/');
    }
  }, [emulateQuery, dispatch, navigate]);

  const filters: FilterInterface[] = useMemo(() => {
    return [
      {
        key: FilterKeys.appPlanActive,
        label: "Active plan",
        filter: (
          <ChoiceList
            title="Active plan"
            selected={[filterValues.appPlanActive.toString()]}
            onChange={v => handleFilterSet("appPlanActive", JSON.parse(v[0]))}
            choices={[
              { label: "TRUE", value: "true" },
              { label: "FALSE", value: "false" },
            ]}
          />
        )
      },
      {
        key: FilterKeys.appPlanName,
        label: "Plan Name",
        filter: (
          <ChoiceList
            title="Plan Name"
            selected={[filterValues.appPlanName]}
            onChange={v => handleFilterSet("appPlanName", v[0])}
            choices={[
              { label: AppPlanNames.HANDBOOK_FREE, value: AppPlanNames.HANDBOOK_FREE },
              { label: AppPlanNames.HANDBOOK_PRO, value: AppPlanNames.HANDBOOK_PRO },
              { label: AppPlanNames.TEST_PLAN, value: AppPlanNames.TEST_PLAN },
            ]}
          />
        )
      },
      {
        key: FilterKeys.appInstalled,
        label: "App installed",
        filter: (
          <ChoiceList
            title="App installed"
            selected={[filterValues.appInstalled.toString()]}
            onChange={v => handleFilterSet("appInstalled", JSON.parse(v[0]))}
            choices={[
              { label: "TRUE", value: "true" },
              { label: "FALSE", value: "false" },
            ]}
          />
        )
      },
    ];
  }, [filterValues, handleFilterSet]);

  const appliedFilters = useMemo(() => {
    const appliedLabels: Record<FilterKeys, string> = {
      appInstalled: `App installed: ${appInstalled ? "TRUE" : "FALSE"}`,
      appPlanActive: `Active plan: ${appPlanActive ? "TRUE" : "FALSE"}`,
      appPlanName: `Plan Name: ${appPlanName}`
    };
    return (Object.entries(filterValues) as [FilterKeys, any][])
      .filter(([, value]) => value !== "")
      .map(([key]) => ({
        key,
        label: appliedLabels[key],
        onRemove: () => handleFilterRemove(key)
      }));
  }, [appInstalled, appPlanActive, appPlanName, filterValues, handleFilterRemove]);

  const cursor = useMemo(() => cursors[page], [cursors, page]);

  useEffect(() => {
    async function getShops() {
      setLoading(true);
      const [frontendKey, direction] = sortBy[0].split(" ") as [string, OrderByDirection];

      const sortKeyMapping: Record<string, string> = {
        appOpenedLast: "appOpenedLast",
        appInstalled: "appInstalled",
        appPlanActive: "appPlanActive",
        appPlanName: "appPlanName",
      };
      const key = sortKeyMapping[frontendKey] || frontendKey;

      const constraints: any[] = [];
      if (shopDomain) {
        constraints.push(where(documentId(), "==", shopDomain.trim()));
      }
      if (filterValues.appPlanActive !== "") {
        constraints.push(where("appPlanActive", "==", filterValues.appPlanActive));
      }
      if (filterValues.appPlanName !== "") {
        constraints.push(where("appPlanName", "==", filterValues.appPlanName));
      }
      if (filterValues.appInstalled !== "") {
        constraints.push(where("appInstalled", "==", filterValues.appInstalled));
      }
      const q = query(
        shopsCollection(),
        ...constraints,
        orderBy(key, direction),
        limit(pageSize + 1),
        ...(cursor ? [startAt(cursor)] : []),
      );

      try {
        const snapshot = await getDocs(q);

        const parseTimestamp = (timestamp: any): string => {
          if (!timestamp) return "";
          if (typeof timestamp.toDate === "function") {
            return timestamp.toDate().toISOString();
          }
          if (timestamp instanceof Date) {
            return timestamp.toISOString();
          }
          if (typeof timestamp === "string") {
            return timestamp;
          }
          if (timestamp && typeof timestamp === "object") {
            const seconds = timestamp.seconds !== undefined ? timestamp.seconds : timestamp._seconds;
            if (seconds !== undefined) {
              return new Date(seconds * 1000).toISOString();
            }
          }
          return "";
        };

        const items = snapshot.docs.map(doc => {
          const data = doc.data() as DbShop;
          return {
            id: doc.id,
            shopName: data.shopName,
            shopDomain: data.shopDomain || doc.id,
            appInstalled: data.appInstalled,
            appPlanActive: data.appPlanActive || false,
            appPlanName: data.appPlanName || null,
            appPlanPrice: data.appPlanPrice || null,
            appPlanTest: data.appPlanTest || false,
            appInstalledAt: parseTimestamp(data.appInstalledAt),
            appInstalledAtFirst: parseTimestamp(data.appInstalledAtFirst),
            appOpenedLast: parseTimestamp(data.appOpenedLast),
            appInstallCount: data.appInstallCount || 1,
            shopCountry: data.shopCountry || "Unknown",
            shopOwnerEmail: data.shopOwnerEmail || "Unknown",
            shopPlanName: data.shopPlanName || "Unknown",
          } as DbShop;
        });

        const newCursor = snapshot.docs[pageSize];

        if (items.length > pageSize) items.pop();

        setCursors(c => ({ ...c, [page + 1]: newCursor }));
        shopsSet(items);
      } catch (err) {
        console.error("Failed to fetch shops:", err);
      } finally {
        setLoading(false);
      }
    }

    getShops();
  }, [cursor, filterValues, page, shopDomain, sortBy]);

  const handleQuerySet = useCallback((str: string) => {
    if (searchQueryTimeout) clearTimeout(searchQueryTimeout);
    setQueryValue(str);
    setLoading(true);
    setSearchQueryTimeout(setTimeout(() => {
      setShopDomain((str || "").toLowerCase());
    }, 500));
  }, [searchQueryTimeout]);

  return (
    <Card padding="0">
      <IndexFilters
        sortOptions={sortOptions}
        sortSelected={sortBy}
        onSort={setSortBy}
        queryPlaceholder="[shop-name].myshopify.com"
        queryValue={queryValue}
        onQueryChange={handleQuerySet}
        onQueryClear={() => setShopDomain("")}
        filters={filters}
        appliedFilters={appliedFilters}
        onClearAll={handleFilterRemoveAll}
        mode={mode}
        setMode={setMode}
        tabs={[{ id: "all", content: "All", isLocked: true }]}
        canCreateNewView={false}
        selected={0}
        loading={loading}
      />
      <IndexTable
        headings={[
          { title: "Shop", id: "shop" },
          { title: "Email", id: "email" },
          { title: "Country", id: "country" },
          { title: "Last seen", id: "appOpenedLast" },
          { title: "Installed", id: "appInstalledAt" },
          { title: "Installs", id: "appInstallCount" },
          { title: "Shopify plan", id: "shopPlanName" },
          { title: "Plan", id: "appPlanName" },
          { title: "Actions", id: "emulate" },
        ]}
        pagination={{
          hasNext: hasNextPage,
          hasPrevious: page > 0,
          onNext: () => setPage(p => p + 1),
          onPrevious: () => setPage(p => p - 1),
        }}
        itemCount={shops.length}
        selectable={false}
      >
        {shops.map((shop, index) => (
          <ShopsRow
            key={shop.id}
            shop={shop}
            position={index}
          />
        ))}
      </IndexTable>
    </Card>
  );
}
